import React, { useState, useEffect, useRef, useLayoutEffect, useMemo } from 'react';
import {
  GoogleAuthProvider, signInWithPopup, linkWithPopup,
  EmailAuthProvider, linkWithCredential, signInWithEmailAndPassword,
  createUserWithEmailAndPassword, signOut, onAuthStateChanged, signInAnonymously,
  sendPasswordResetEmail
} from 'firebase/auth';
import { collection, addDoc, deleteDoc, updateDoc, doc, onSnapshot, serverTimestamp, Timestamp, writeBatch, query, where, getDocs, orderBy } from 'firebase/firestore';
import { Settings, WifiOff } from 'lucide-react';

import { auth, db, isConfigValid, appId } from './config/firebase';
import { generateId, toDateObj, processHardModeWord } from './utils/helpers';
import useScript from './hooks/useScript';
import { callOpenAI } from './services/aiService';
import { updateArticleSRS } from './services/srsService';

import Toast from './components/ui/Toast';
import ErrorBoundary from './components/ui/ErrorBoundary';
import DeleteConfirmModal from './components/modals/DeleteConfirmModal';
import InputModal from './components/modals/InputModal';
import AIGenerateModal from './components/modals/AIGenerateModal';
import SaveLocationModal from './components/modals/SaveLocationModal';
import ConfirmModal from './components/modals/ConfirmModal';
import AuthModal from './components/modals/AuthModal';

import HomeView from './views/HomeView';
import EditView from './views/EditView';
import TypingView from './views/TypingView';
import ResultView from './views/ResultView';

const STORAGE_KEY = 'typeflow_local_data_v1';

function App() {
  useScript('https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js');

  // --- State ---
  const [skipCloud, setSkipCloud] = useState(false);
  const [localData, setLocalData] = useState({ articles: [], folders: [] });
  const [user, setUser] = useState(null);
  const [cloudArticles, setCloudArticles] = useState([]);
  const [cloudFolders, setCloudFolders] = useState([]);

  // UI State
  const [toast, setToast] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [isAiGenerateModalOpen, setIsAiGenerateModalOpen] = useState(false);
  const [isSaveLocationModalOpen, setIsSaveLocationModalOpen] = useState(false);
  const [isBatchMoveModalOpen, setIsBatchMoveModalOpen] = useState(false);
  const [batchMoveTargetIds, setBatchMoveTargetIds] = useState([]);
  const [confirmModal, setConfirmModal] = useState(null); // { title, desc, onConfirm, isDanger }
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  const [step, setStep] = useState('upload');
  const [currentFolderId, setCurrentFolderId] = useState(null);

  const [currentArticleId, setCurrentArticleId] = useState(null);
  const [articleTitle, setArticleTitle] = useState('');
  const [rawText, setRawText] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState('');

  const [parsedData, setParsedData] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const [difficulty, setDifficulty] = useState('easy');

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [errorIndices, setErrorIndices] = useState(new Set());
  const [mistakenWords, setMistakenWords] = useState(new Set());

  const [srsResult, setSrsResult] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [tempRenameTitle, setTempRenameTitle] = useState('');
  const [originalSnapshot, setOriginalSnapshot] = useState(null); // { title, content }

  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const charRefs = useRef([]);
  const [cursorStyle, setCursorStyle] = useState({ left: 0, top: 0, height: 0 });
  const [xtraData, setXtraData] = useState({});
  // Cache storage: { "English Sentence": "Traditional Chinese Translation" }
  const [translationCache, setTranslationCache] = useState({});
  const fileInputRef = useRef(null);

  // --- Init Local Data ---
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) { try { setLocalData(JSON.parse(stored)); } catch (e) { console.error(e); } }
  }, []);

  const saveLocalData = (newData) => {
    setLocalData(newData);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newData));
  };

  // --- Firebase Auth & Listeners ---
  useEffect(() => {
    if (!isConfigValid || skipCloud) return;
    if (!auth) return;
    const unsubscribe = onAuthStateChanged(auth, setUser);
    signInAnonymously(auth).catch(console.error);
    return () => unsubscribe();
  }, [skipCloud]);

  useEffect(() => {
    if (!user) return;
    const unsubA = onSnapshot(collection(db, 'artifacts', appId, 'users', user.uid, 'articles'), (s) => {
      setCloudArticles(s.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
    });
    const unsubF = onSnapshot(collection(db, 'artifacts', appId, 'users', user.uid, 'folders'), (s) => {
      setCloudFolders(s.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
    });
    return () => { unsubA(); unsubF(); };
  }, [user]);

  // --- Derived Data ---
  const savedArticles = user ? cloudArticles : localData.articles;
  const folders = user ? cloudFolders : localData.folders;

  const currentTypingWordId = useMemo(() => {
    if (step !== 'typing' || !parsedData.length) return -1;
    const currentIndex = userInput.length;
    if (currentIndex >= parsedData.length) return parsedData[parsedData.length - 1].wordId;
    return parsedData[currentIndex].wordId;
  }, [userInput, parsedData, step]);

  const currentFolder = useMemo(() => folders.find(f => f.id === currentFolderId), [folders, currentFolderId]);
  const visibleFolders = useMemo(() => folders.filter(f => (f.parentId || null) === (currentFolderId || null)), [folders, currentFolderId]);
  const visibleArticles = useMemo(() => savedArticles.filter(article => (article.folderId || null) === (currentFolderId || null)), [savedArticles, currentFolderId]);
  const dailyReviewArticles = useMemo(() => savedArticles.filter(a => { if (!a.nextReviewDate) return false; return toDateObj(a.nextReviewDate) <= new Date(); }), [savedArticles]);
  const currentFolderName = useMemo(() => currentFolder ? currentFolder.name : "我的文章庫", [currentFolder]);

  // Effects
  useLayoutEffect(() => {
    if (step !== 'typing') return;
    const targetIndex = userInput.length;
    const targetNode = charRefs.current[targetIndex];
    if (targetNode && containerRef.current) {
      setCursorStyle({ left: targetNode.offsetLeft, top: targetNode.offsetTop, height: targetNode.offsetHeight || 36 });
    }
  }, [userInput, step, parsedData]);

  const showToast = (message, type = 'success') => setToast({ message, type });

  // --- Handlers ---
  const checkUnsavedChanges = () => {
    if (['edit', 'typing', 'result'].includes(step)) {
      // Smart Check: If new article and title duplicates existing one, skip prompt (User Request)
      if (!currentArticleId && articleTitle.trim()) {
        const isDuplicate = savedArticles.some(a => a.title === articleTitle.trim());
        if (isDuplicate) return false;
      }

      // For existing articles, compare with snapshot
      if (currentArticleId && originalSnapshot) {
        if (articleTitle.trim() !== originalSnapshot.title || rawText.trim() !== originalSnapshot.content) {
          return true;
        }
        return false;
      }

      // Fallback for new articles or missing snapshot (shouldn't happen often if flow is correct)
      if (articleTitle.trim() || rawText.trim()) return true;
    }
    return false;
  };

  const goHome = () => {
    if (checkUnsavedChanges()) {
      setConfirmModal({
        title: "尚未儲存",
        desc: "文章尚未儲存，是否要儲存？",
        confirmText: "儲存",
        cancelText: "不儲存離開",
        isDanger: false, // Not danger because primary action is Save
        onConfirm: () => {
          setConfirmModal(null);
          setIsSaveLocationModalOpen(true); // Open save modal
        },
        onCancel: () => {
          setConfirmModal(null);
          forceGoHome();
        }
      });
      return;
    }
    forceGoHome();
  };

  const forceGoHome = () => {
    setStep('upload'); setRawText(''); setArticleTitle(''); setUserInput(''); setDifficulty('easy'); setCurrentArticleId(null); setSelectedFolderId(''); setOriginalSnapshot(null); setTranslationCache({});
  };

  const handleExport = () => {
    const backupData = { version: 1, timestamp: Date.now(), folders, articles: savedArticles };
    const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `typeflow_backup.json`; a.click(); URL.revokeObjectURL(url);
    showToast("匯出成功！");
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const imported = JSON.parse(event.target.result);
        if (!imported.folders || !imported.articles) throw new Error("格式錯誤");

        if (user) {
          const batch = writeBatch(db);
          let count = 0;
          if (imported.folders) imported.folders.forEach(f => {
            const ref = doc(db, 'artifacts', appId, 'users', user.uid, 'folders', f.id);
            const { id, ...fData } = f;
            if (fData.createdAt?.seconds) fData.createdAt = new Timestamp(fData.createdAt.seconds, fData.createdAt.nanoseconds);
            batch.set(ref, fData, { merge: true }); count++;
          });
          if (imported.articles) imported.articles.forEach(a => {
            const ref = doc(db, 'artifacts', appId, 'users', user.uid, 'articles', a.id);
            const { id, ...aData } = a;
            if (aData.createdAt?.seconds) aData.createdAt = new Timestamp(aData.createdAt.seconds, aData.createdAt.nanoseconds);
            if (aData.nextReviewDate?.seconds) aData.nextReviewDate = new Timestamp(aData.nextReviewDate.seconds, aData.nextReviewDate.nanoseconds);
            if (aData.lastReviewed?.seconds) aData.lastReviewed = new Timestamp(aData.lastReviewed.seconds, aData.lastReviewed.nanoseconds);
            batch.set(ref, aData, { merge: true }); count++;
          });
          await batch.commit();
          showToast(`成功匯入 ${count} 筆資料至雲端！`);
        } else {
          const newFolders = [...folders];
          const newArticles = [...savedArticles];
          if (imported.folders) imported.folders.forEach(f => { if (!newFolders.find(nf => nf.id === f.id)) newFolders.push(f); });
          if (imported.articles) imported.articles.forEach(a => { if (!newArticles.find(na => na.id === a.id)) newArticles.push(a); });
          saveLocalData({ folders: newFolders, articles: newArticles });
          showToast(`成功匯入資料至本機！`);
        }
      } catch (err) { console.error(err); showToast("匯入失敗：格式錯誤", "error"); }
    };
    reader.readAsText(file); e.target.value = null;
  };

  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      if (user && user.isAnonymous) {
        // Try to link first
        try {
          await linkWithPopup(user, provider);
          showToast("✅ 帳號綁定成功");
        } catch (linkError) {
          if (linkError.code === 'auth/credential-already-in-use') {
            // If already in use, we must sign in (which replaces the anon user)
            // Ideally we might want to merge data, but for now just sign in
            await signInWithPopup(auth, provider);
            showToast("✅ 登入成功 (切換至現有帳號)");
          } else {
            throw linkError;
          }
        }
      } else {
        await signInWithPopup(auth, provider);
        showToast("✅ Google 登入成功");
      }
    } catch (error) {
      console.error("Google Login Error:", error);
      showToast("登入失敗: " + error.message, "error");
      throw error; // Rethrow for modal to handle
    }
  };

  const handleEmailLogin = async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      showToast("✅ 登入成功");
    } catch (error) {
      console.error("Email Login Error:", error);
      throw error;
    }
  };

  const handleEmailRegister = async (email, password) => {
    try {
      if (user && user.isAnonymous) {
        const credential = EmailAuthProvider.credential(email, password);
        try {
          await linkWithCredential(user, credential);
          showToast("✅ 註冊並綁定成功");
        } catch (linkError) {
          if (linkError.code === 'auth/credential-already-in-use') {
            throw new Error("此 Email 已被其他帳號使用");
          } else if (linkError.code === 'auth/email-already-in-use') {
            throw new Error("此 Email 已被註冊");
          }
          throw linkError;
        }
      } else {
        await createUserWithEmailAndPassword(auth, email, password);
        showToast("✅ 註冊成功");
      }
    } catch (error) {
      console.error("Register Error:", error);
      throw error;
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      showToast("已登出");
    } catch (error) {
      console.error("Logout Error:", error);
      showToast("登出失敗", "error");
    }
  };

  const handlePasswordReset = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      showToast("重設信已發送");
    } catch (error) {
      console.error("Reset Password Error:", error);
      throw error;
    }
  };

  const handleWordClick = (clickedWordId) => {
    if (clickedWordId === -1) return;
    const wordChars = parsedData.filter(item => item.wordId === clickedWordId);
    const fullWord = wordChars.map(item => item.char).join('').replace(/[^\w']/g, '');
    if (fullWord) { const u = new SpeechSynthesisUtterance(fullWord); u.lang = 'en-US'; window.speechSynthesis.speak(u); }
    inputRef.current?.focus();
  };

  const handleAiGenerate = async (topic, level) => {
    setIsAiGenerating(true);
    const userPrompt = `Generate a short English practice article (about 60-100 words).\nTopic: "${topic}"\nDifficulty: "${level}"\nReturn ONLY raw text.`;
    try {
      const text = await callOpenAI(userPrompt, "You are an English teacher.");
      if (text) {
        setRawText(text.trim()); setArticleTitle(`${topic} (${level})`); setCurrentArticleId(null); setStep('edit'); setIsAiGenerateModalOpen(false); showToast("✨ 文章生成成功！");
      } else { showToast("生成失敗", "error"); }
    } catch (e) { showToast(`AI 錯誤: ${e.message}`, "error"); } finally { setIsAiGenerating(false); }
  };

  const handleAiPolish = async () => {
    if (!rawText.trim()) return;
    setIsAiGenerating(true);
    try {
      const fixedText = await callOpenAI(`Correct this text:\n"${rawText}"`, "You are an editor. Return ONLY corrected text.");
      if (fixedText) { setRawText(fixedText.trim()); showToast("✨ 文章已優化！"); }
    } catch (e) { showToast(`修復失敗: ${e.message}`, "error"); } finally { setIsAiGenerating(false); }
  };

  const handleFileUpload = (e) => { const file = e.target.files[0]; if (file) { setArticleTitle(file.name.replace(/\.[^/.]+$/, "")); processImage(file); } };
  const processImage = async (file) => {
    if (!window.Tesseract) { showToast("OCR 載入中...", "error"); return; }
    setIsProcessing(true);
    const imageUrl = URL.createObjectURL(file);
    try {
      const result = await window.Tesseract.recognize(imageUrl, 'eng');
      let text = result.data.text;
      text = text.replace(/\n\s*\n/g, '___PARAGRAPH___').replace(/\n/g, ' ').replace(/___PARAGRAPH___/g, '\n\n').replace(/ {2,}/g, ' ').trim();
      setRawText(text); setStep('edit');
    } catch (err) { console.error(err); showToast("辨識失敗", "error"); } finally { URL.revokeObjectURL(imageUrl); setIsProcessing(false); }
  };

  const handleCreateFolder = async (folderName, parentId) => {
    if (!folderName.trim()) return;
    if (!user || user.isAnonymous) {
      showToast("請先登入以建立資料夾", "error");
      setIsAuthModalOpen(true);
      return;
    }
    const targetParentId = parentId !== undefined ? parentId : (currentFolderId || null);

    await addDoc(collection(db, 'artifacts', appId, 'users', user.uid, 'folders'), { name: folderName.trim(), parentId: targetParentId, createdAt: serverTimestamp() });
    showToast("資料夾已建立"); setIsCreateFolderModalOpen(false);
  };

  const handleDeleteFolder = async () => {
    if (!deleteTarget) return;
    if (!user || user.isAnonymous) {
      showToast("請先登入以刪除資料夾", "error");
      setIsAuthModalOpen(true);
      return;
    }
    try { await deleteDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'folders', deleteTarget.id)); showToast("資料夾已刪除"); } catch (e) { showToast("刪除失敗", "error"); }
    setDeleteTarget(null);
  };

  const handleSaveArticle = async (folderId, stayOnPage = false) => {
    handleSaveArticleRefined(folderId, stayOnPage);
  };

  // We need to refactor handleSaveArticle slightly to capture ID for stayOnPage
  // Rerunning this block to include logic update.
  const handleSaveArticleRefined = async (folderId, stayOnPage = false, overrideId = null) => {
    if (!articleTitle.trim() || !rawText.trim()) { showToast("標題不可為空", "error"); return; }

    const targetFolderId = folderId !== undefined ? folderId : selectedFolderId;
    const targetId = overrideId || currentArticleId;

    const isTitleDuplicate = savedArticles.some(a => a.title === articleTitle.trim() && a.folderId === (targetFolderId || null) && a.id !== targetId);
    if (isTitleDuplicate) { showToast("標題重複", "error"); return; }

    // Prepare data to save, including translation cache
    const dataToSave = {
      title: articleTitle.trim(),
      content: rawText.trim(),
      folderId: targetFolderId || null,
      xtraTranslations: translationCache // Save the cache
    };

    let newId = targetId;

    if (user && !user.isAnonymous) {
      try {
        if (targetId) { await updateDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'articles', targetId), dataToSave); showToast("✅ 文章已更新"); }
        else { const ref = await addDoc(collection(db, 'artifacts', appId, 'users', user.uid, 'articles'), { ...dataToSave, createdAt: serverTimestamp() }); newId = ref.id; showToast("✅ 文章已儲存"); }
      } catch (e) { showToast("儲存失敗", "error"); return; }
    } else {
      // Local Save
      if (targetId) {
        const updated = localData.articles.map(a => a.id === targetId ? { ...a, ...dataToSave } : a);
        saveLocalData({ ...localData, articles: updated });
        showToast("✅ 文章已更新 (離線)");
      } else {
        newId = generateId();
        const newArticle = { id: newId, ...dataToSave, createdAt: Date.now(), srsIntervalIndex: -1 };
        saveLocalData({ ...localData, articles: [...localData.articles, newArticle] });
        showToast("✅ 文章已儲存 (離線)");
      }
    }

    localStorage.setItem('lastSavedFolderId', targetFolderId || '');
    setSelectedFolderId(targetFolderId || '');
    setIsSaveLocationModalOpen(false);

    // Update snapshot to new saved state
    setOriginalSnapshot({ title: articleTitle.trim(), content: rawText.trim() });

    if (stayOnPage) {
      if (!currentArticleId && newId) setCurrentArticleId(newId);
      if (overrideId && currentArticleId !== overrideId) setCurrentArticleId(overrideId);
    } else {
      forceGoHome();
    }
  };

  const handleSaveClick = () => {
    // Smart Save: If new article and title matches existing, auto-link and save
    if (!currentArticleId && articleTitle.trim()) {
      const duplicate = savedArticles.find(a => a.title === articleTitle.trim());
      if (duplicate) {
        handleSaveArticleRefined(duplicate.folderId, true, duplicate.id);
        showToast("發現同名檔案，已自動覆蓋儲存");
        return;
      }
    }

    // If editing existing article, default to its folder.
    // If new, default to last saved folder or root.
    if (!currentArticleId) {
      const lastSaved = localStorage.getItem('lastSavedFolderId');
      if (lastSaved) setSelectedFolderId(lastSaved);
    }
    setIsSaveLocationModalOpen(true);
  };

  const handleUpdateArticleTitle = async (articleId) => {
    if (!tempRenameTitle.trim()) return;
    if (!user || user.isAnonymous) {
      showToast("請先登入以更名", "error");
      setIsAuthModalOpen(true);
      return;
    }
    try { await updateDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'articles', articleId), { title: tempRenameTitle.trim() }); showToast("更名成功"); } catch (e) { showToast("更名失敗", "error"); }
    setRenamingId(null);
  };

  const handleBatchMove = async (targetFolderId) => {
    if (batchMoveTargetIds.length === 0) return;
    if (!user || user.isAnonymous) {
      showToast("請先登入以移動文章", "error");
      setIsAuthModalOpen(true);
      return;
    }

    try {
      const batch = writeBatch(db);
      batchMoveTargetIds.forEach(id => {
        const ref = doc(db, 'artifacts', appId, 'users', user.uid, 'articles', id);
        batch.update(ref, { folderId: targetFolderId || null });
      });
      await batch.commit();
      showToast(`已移動 ${batchMoveTargetIds.length} 篇文章`);
    } catch (e) { showToast("移動失敗", "error"); }

    setIsBatchMoveModalOpen(false);
    setBatchMoveTargetIds([]);
  };

  const handleDirectMove = async (id, targetFolderId, type = 'article') => {
    if (!user || user.isAnonymous) {
      showToast("請先登入以移動項目", "error");
      setIsAuthModalOpen(true);
      return;
    }

    if (type === 'folder') {
      if (id === targetFolderId) return;
      try { await updateDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'folders', id), { parentId: targetFolderId || null }); showToast("資料夾已移動"); } catch (e) { showToast("移動失敗", "error"); }
    } else {
      try { await updateDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'articles', id), { folderId: targetFolderId || null }); showToast("文章已移動"); } catch (e) { showToast("移動失敗", "error"); }
    }
  };

  const handleDeleteArticle = async () => {
    if (!deleteTarget) return;
    if (!user || user.isAnonymous) {
      showToast("請先登入以刪除文章", "error");
      setIsAuthModalOpen(true);
      return;
    }
    try { await deleteDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'articles', deleteTarget.id)); showToast("🗑️ 文章已刪除"); } catch (e) { showToast("刪除失敗", "error"); }
    setDeleteTarget(null);
  };

  const handleLoadArticle = (article) => {
    if (renamingId) return;
    setArticleTitle(article.title);
    setRawText(article.content);
    setCurrentArticleId(article.id);
    setSelectedFolderId(article.folderId || '');
    setOriginalSnapshot({ title: article.title, content: article.content });

    // Load translations if available
    setTranslationCache(article.xtraTranslations || {});

    setStep('edit');
  };
  const handleNavigateUp = () => { setCurrentFolderId(currentFolder?.parentId || null); };

  // --- Game Logic ---
  const parseTextToData = async () => {
    try {
      if (!rawText.trim()) {
        showToast("請先輸入文章內容", "error");
        return;
      }
      const finalChars = [];
      let isHighlightMode = false;
      let currentWordId = 0;
      let currentSentenceId = 0;
      let wordBuffer = [];

      // Helper to flush word buffer
      const flushWord = () => {
        if (wordBuffer.length === 0) return;
        const wordString = wordBuffer.map(c => c.char).join('');
        let displayString = wordString;
        if (difficulty === 'hard') displayString = processHardModeWord(wordString);
        if (difficulty === 'xtra') displayString = '_'.repeat(wordString.length);

        wordBuffer.forEach((item, idx) => {
          finalChars.push({ ...item, displayChar: displayString[idx] || item.char });
        });
        wordBuffer = [];
        currentWordId++;
      };

      // 1. First pass: Parse chars and words as before, but also track raw sentences for ID assignment
      // Actually, we can do it in one pass if we are careful, or pre-split sentences.
      // Pre-splitting sentences might be easier to assign IDs.

      // Let's split by sentence delimiters first to identify ranges?
      // Or just track it on the fly.
      // Delimiters: . ? ! followed by space or newline, or just . ? ! at end of string.
      // Simple regex split might be easier to get the text chunks, then process chunks.

      // Regex to split but keep delimiters: /([.?!]+[\s\n]*)/
      // But we need to preserve exact characters for typing.

      // Let's stick to char-by-char but detect sentence end.

      for (let i = 0; i < rawText.length; i++) {
        const char = rawText[i];

        // Check for sentence end (heuristic)
        // If previous char was .?! and current is space/newline, or if current is .?! and next is EOF
        // Actually, let's assign sentenceId based on accumulation.

        if (char === '*') { isHighlightMode = !isHighlightMode; continue; }

        const isWordChar = !/[\s\n\t]/.test(char);

        if (!isWordChar) {
          flushWord();
          finalChars.push({
            char,
            isHighlight: isHighlightMode,
            isNewline: char === '\n',
            wordId: -1,
            displayChar: char,
            sentenceId: currentSentenceId
          });

          // Check if this space/newline marks end of sentence
          // If the LAST non-space char was a punctuation
          // We need to look back at `finalChars` ignoring spaces
          let backIdx = finalChars.length - 2; // -1 is the current char we just added
          while (backIdx >= 0 && (finalChars[backIdx].wordId === -1)) {
            backIdx--;
          }
          if (backIdx >= 0) {
            const lastChar = finalChars[backIdx].char;
            if (['.', '!', '?'].includes(lastChar)) {
              // It was a sentence end.
              // BUT, we want the punctuation to be part of the PREVIOUS sentence.
              // The space after it (current char) should probably be part of the PREVIOUS sentence or NEXT?
              // Usually space after period belongs to the previous sentence or is a separator.
              // Let's say it belongs to the previous.
              // So we increment AFTER this char.
              currentSentenceId++;
            }
          }

        } else {
          wordBuffer.push({
            char,
            isHighlight: isHighlightMode,
            isNewline: false,
            wordId: currentWordId,
            sentenceId: currentSentenceId
          });
        }
      }
      flushWord();

      // Handle Xtra Mode Translation
      if (difficulty === 'xtra') {
        setIsProcessing(true);

        const sentences = [];
        let currentSId = -1;
        let currentSText = '';

        finalChars.forEach(c => {
          if (c.sentenceId !== currentSId) {
            if (currentSId !== -1 && currentSText.trim()) {
              sentences.push({ id: currentSId, text: currentSText.trim() });
            }
            currentSId = c.sentenceId;
            currentSText = '';
          }
          currentSText += c.char;
        });
        if (currentSId !== -1 && currentSText.trim()) {
          sentences.push({ id: currentSId, text: currentSText.trim() });
        }

        // Check translation cache
        const newXtraData = {};
        const missingSentences = [];

        sentences.forEach(s => {
          if (translationCache[s.text]) {
            newXtraData[s.id] = translationCache[s.text];
          } else {
            missingSentences.push(s);
          }
        });

        let updatedCache = { ...translationCache };

        // Query API only for missing sentences
        if (missingSentences.length > 0) {
          const textsToTranslate = missingSentences.map(s => s.text);
          const prompt = `Translate the following English sentences into Traditional Chinese (Taiwan). Return ONLY a JSON array of strings matching the input order. Input: ${JSON.stringify(textsToTranslate)}`;

          try {
            const resultStr = await callOpenAI(prompt, "You are a translator. Return valid JSON array only.");
            const jsonStr = resultStr.replace(/```json/g, '').replace(/```/g, '').trim();
            const translations = JSON.parse(jsonStr);

            if (Array.isArray(translations)) {
              missingSentences.forEach((s, i) => {
                if (translations[i]) {
                  newXtraData[s.id] = translations[i];
                  updatedCache[s.text] = translations[i];
                }
              });
              setTranslationCache(updatedCache);
            }
          } catch (e) {
            console.error("Translation failed", e);
            showToast("部分翻譯生成失敗", "error");
          }
        }

        setXtraData(newXtraData);

        // Auto-save translation cache if article is saved
        if (currentArticleId && (user && !user.isAnonymous)) {
          // We do a "silent" update to save the cache if changed
          if (missingSentences.length > 0) {
            try {
              await updateDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'articles', currentArticleId), { xtraTranslations: updatedCache });
            } catch (e) { console.error("Auto-save translations failed", e); }
          }
        } else if (currentArticleId && (!user || user.isAnonymous)) {
          // Local auto-save
          if (missingSentences.length > 0) {
            const updated = localData.articles.map(a => a.id === currentArticleId ? { ...a, xtraTranslations: updatedCache } : a);
            saveLocalData({ ...localData, articles: updated });
          }
        }

        setIsProcessing(false);
      }

      setParsedData(finalChars); setUserInput(''); setErrorIndices(new Set()); setMistakenWords(new Set()); charRefs.current = []; setStep('typing'); setStartTime(Date.now()); setTimeout(() => inputRef.current?.focus(), 100);
    } catch (e) {
      console.error("Parse Error:", e);
      showToast("解析文章失敗: " + e.message, "error");
      setIsProcessing(false);
    }
  };

  const finishGame = async () => {
    const endTime = Date.now(); setEndTime(endTime);
    const totalChars = parsedData.length; const errorCount = errorIndices.size; const accuracy = totalChars > 0 ? ((totalChars - errorCount) / totalChars * 100) : 0;
    if (currentArticleId) {
      const res = await updateArticleSRS(currentArticleId, accuracy, user, localData, saveLocalData);
      setSrsResult(res);
    } else {
      setSrsResult(null);
    }
    setStep('result');
  };

  const handleTyping = (e) => {
    const val = e.target.value; const idx = val.length - 1;
    if (val.length < userInput.length) { setUserInput(val); return; }
    const target = parsedData[idx]?.char; const input = val[idx];
    if (target && input !== target) { setErrorIndices(prev => new Set(prev).add(idx)); identifyMistakenWord(idx); }
    setUserInput(val);

    // Medium Mode: Dynamic Flash & TTS
    if (difficulty === 'medium') {
      const currentItem = parsedData[idx];
      // Check if start of word
      const isWordStart = currentItem && currentItem.wordId !== -1 && (idx === 0 || parsedData[idx - 1].wordId !== currentItem.wordId);

      if (isWordStart) {
        // Get full word
        const wordItems = parsedData.filter(i => i.wordId === currentItem.wordId);
        const wordStr = wordItems.map(i => i.char).join('');

        // TTS (Skip short words <= 3 chars)
        if (wordStr.replace(/[^a-zA-Z]/g, '').length > 3) {
          const u = new SpeechSynthesisUtterance(wordStr);
          u.lang = 'en-US';
          u.rate = 1.1;
          window.speechSynthesis.speak(u);
        }

        // Masking: Hide remaining chars of this word
        setParsedData(prev => prev.map((item, i) => {
          if (item.wordId === currentItem.wordId && i > idx) {
            return { ...item, displayChar: '_' };
          }
          return item;
        }));
      }
    }

    // Reveal Word on Complete (Medium & Hard)
    if (difficulty === 'medium' || difficulty === 'hard') {
      const currentItem = parsedData[idx];
      if (currentItem && currentItem.wordId !== -1) {
        // Check if this is the last char of the word
        const nextItem = parsedData[idx + 1];
        const isWordEnd = !nextItem || nextItem.wordId !== currentItem.wordId;

        if (isWordEnd) {
          // Check if typed correctly
          // We need to check the substring of val corresponding to this word
          // Find start index of this word
          let start = idx;
          while (start > 0 && parsedData[start - 1].wordId === currentItem.wordId) {
            start--;
          }
          const typedWord = val.slice(start, idx + 1);
          const targetWord = parsedData.slice(start, idx + 1).map(i => i.char).join('');

          if (typedWord === targetWord) {
            // Reveal the word
            setParsedData(prev => prev.map(item => {
              if (item.wordId === currentItem.wordId) {
                return { ...item, displayChar: item.char };
              }
              return item;
            }));
          }
        }
      }
    }

    if (val.length === parsedData.length) { finishGame(); }
  };

  const identifyMistakenWord = (index) => {
    let start = index; let end = index;
    while (start > 0 && parsedData[start - 1].char !== ' ' && parsedData[start - 1].char !== '\n') start--;
    while (end < parsedData.length - 1 && parsedData[end + 1].char !== ' ' && parsedData[end + 1].char !== '\n') end++;
    let word = ''; for (let i = start; i <= end; i++) word += parsedData[i].char;
    const cleanWord = word.replace(/[^\w']/g, ''); if (cleanWord.length > 1) setMistakenWords(prev => new Set(prev).add(cleanWord));
  };

  const calculateStats = () => {
    const totalChars = parsedData.length; const errorCount = errorIndices.size;
    const accuracy = totalChars > 0 ? ((totalChars - errorCount) / totalChars * 100).toFixed(1) : 0;
    const durationSec = (endTime - startTime) / 1000;
    const wpm = durationSec > 0 ? Math.round(((totalChars / 5) / durationSec) * 60) : 0;
    return { accuracy, wpm, errorCount };
  };

  // --- Render Config Check ---
  if (!isConfigValid && !skipCloud) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-6 text-center font-sans">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full border border-slate-200">
          <div className="w-16 h-16 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4"><Settings className="w-8 h-8" /></div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">TypeFlow 設定</h1>
          <p className="text-slate-500 mb-6 text-sm">尚未偵測到 Firebase 設定。您可以選擇填寫設定以啟用雲端同步，或直接使用離線模式。</p>
          <button onClick={() => setSkipCloud(true)} className="w-full py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"><WifiOff className="w-4 h-4" /> 暫時略過 (使用離線模式)</button>
        </div>
      </div>
    );
  }

  return (
    <div className={isDarkMode ? 'dark' : ''}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <DeleteConfirmModal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={deleteTarget?.type === 'folder' ? handleDeleteFolder : handleDeleteArticle} title={deleteTarget?.type === 'folder' ? "刪除資料夾？" : "刪除文章？"} desc={deleteTarget?.type === 'folder' ? "資料夾刪除後，內部的文章將會保留在全部文章中。" : "此動作無法復原。"} />
      <InputModal isOpen={isCreateFolderModalOpen} onClose={() => setIsCreateFolderModalOpen(false)} onConfirm={handleCreateFolder} title="新增資料夾" placeholder="請輸入資料夾名稱" />
      <AIGenerateModal isOpen={isAiGenerateModalOpen} onClose={() => setIsAiGenerateModalOpen(false)} onGenerate={handleAiGenerate} isGenerating={isAiGenerating} />
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onGoogleLogin={handleGoogleLogin}
        onEmailLogin={handleEmailLogin}
        onEmailRegister={handleEmailRegister}
        onPasswordReset={handlePasswordReset}
      />

      {confirmModal && (
        <ConfirmModal
          isOpen={!!confirmModal}
          onClose={() => setConfirmModal(null)}
          onConfirm={confirmModal?.onConfirm}
          onCancel={confirmModal?.onCancel}
          title={confirmModal?.title}
          desc={confirmModal?.desc}
          confirmText={confirmModal?.confirmText}
          cancelText={confirmModal?.cancelText}
          isDanger={confirmModal?.isDanger}
        />
      )}

      {/* Save Modal */}
      <SaveLocationModal
        isOpen={isSaveLocationModalOpen}
        onClose={() => setIsSaveLocationModalOpen(false)}
        onConfirm={(fid) => handleSaveArticleRefined(fid, false)}
        folders={folders}
        articles={savedArticles}
        initialFolderId={selectedFolderId}
        onCreateFolder={handleCreateFolder}
        title="儲存文章"
        confirmText="儲存至此"
      />

      {/* Move Modal */}
      <SaveLocationModal
        isOpen={isBatchMoveModalOpen}
        onClose={() => { setIsBatchMoveModalOpen(false); setBatchMoveTargetIds([]); }}
        onConfirm={handleBatchMove}
        folders={folders}
        articles={savedArticles}
        initialFolderId={currentFolderId}
        onCreateFolder={handleCreateFolder}
        title={`移動 ${batchMoveTargetIds.length} 篇文章`}
        confirmText="移動至此"
      />

      <input type="file" ref={fileInputRef} className="hidden" accept=".json" onChange={handleImport} />

      {step === 'upload' && (
        <HomeView
          isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode}
          dailyReviewArticles={dailyReviewArticles} savedArticles={savedArticles}
          currentFolderId={currentFolderId} setCurrentFolderId={setCurrentFolderId} currentFolderName={currentFolderName}
          visibleFolders={visibleFolders} visibleArticles={visibleArticles}
          renamingId={renamingId} setRenamingId={setRenamingId}
          tempRenameTitle={tempRenameTitle} setTempRenameTitle={setTempRenameTitle}
          onUpdateArticleTitle={handleUpdateArticleTitle}
          onLoadArticle={handleLoadArticle}
          onDeleteRequest={setDeleteTarget}
          onMoveRequest={(id) => { setBatchMoveTargetIds([id]); setIsBatchMoveModalOpen(true); }}
          onBatchMoveRequest={(ids) => { setBatchMoveTargetIds(ids); setIsBatchMoveModalOpen(true); }}
          onDirectMoveRequest={handleDirectMove}
          onExport={handleExport}
          onImport={() => fileInputRef.current?.click()}
          onFileUpload={handleFileUpload}
          onManualInput={() => { setRawText(''); setArticleTitle(''); setCurrentArticleId(null); setStep('edit'); setOriginalSnapshot(null); }}
          onAiGenerateClick={() => setIsAiGenerateModalOpen(true)}
          onCreateFolderClick={() => setIsCreateFolderModalOpen(true)}
          onNavigateUp={handleNavigateUp}
          fileInputRef={fileInputRef}
          user={user}
          onLoginClick={() => setIsAuthModalOpen(true)}
          onLogoutClick={handleLogout}
        />
      )}

      {step === 'edit' && (
        <EditView
          articleTitle={articleTitle} setArticleTitle={setArticleTitle}
          rawText={rawText} setRawText={setRawText}
          folders={folders} selectedFolderId={selectedFolderId} setSelectedFolderId={setSelectedFolderId}
          difficulty={difficulty} setDifficulty={setDifficulty}
          isAiGenerating={isAiGenerating}
          onAiPolish={handleAiPolish}
          onSave={(stay) => {
            if (stay) handleSaveArticleRefined(undefined, true);
            else handleSaveClick();
          }}
          onGoHome={goHome}
          onParse={parseTextToData}
          isProcessing={isProcessing}
        />
      )}

      {step === 'typing' && (
        <TypingView
          articleTitle={articleTitle}
          difficulty={difficulty}
          userInput={userInput}
          parsedData={parsedData}
          cursorStyle={cursorStyle}
          onTyping={handleTyping}
          onGoHome={goHome}
          inputRef={inputRef}
          containerRef={containerRef}
          charRefs={charRefs}
          currentTypingWordId={currentTypingWordId}
          handleWordClick={handleWordClick}
          xtraData={xtraData}
        />
      )}

      {step === 'result' && (
        <ResultView
          difficulty={difficulty}
          stats={calculateStats()}
          srsResult={srsResult}
          onRetry={() => { setUserInput(''); setErrorIndices(new Set()); setStep('typing'); setStartTime(Date.now()); }}
          onGoHome={goHome}
        />
      )}
    </div>
  );
}

export default function AppWithBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}